import { useEffect, useRef, useState } from "preact/hooks";
import {
  SITE_LOCALES,
  pathForLocale,
  type PageContent,
  type SiteLocale,
} from "../content/index.js";
import { CheckIcon, ChevronDownIcon } from "../shared/icons.js";

const SHORT: Record<SiteLocale, string> = { "pt-BR": "PT", en: "EN", es: "ES" };
const NAME: Record<SiteLocale, string> = { "pt-BR": "Português", en: "English", es: "Español" };

export interface LanguagePickerProps {
  readonly content: PageContent;
  readonly locale: SiteLocale;
}

export function LanguagePicker({ content, locale }: LanguagePickerProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div class={open ? "lang is-open" : "lang"} ref={root}>
      <button
        ref={button}
        type="button"
        class="lang-button"
        aria-label={content.nav.language}
        aria-expanded={open}
        aria-controls="lang-menu"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">{SHORT[locale]}</span>
        <span class="lang-chevron">
          <ChevronDownIcon size={14} />
        </span>
      </button>
      <ul class="lang-menu" id="lang-menu">
        {SITE_LOCALES.map((option) => (
          <li key={option}>
            <a
              class="lang-option"
              href={pathForLocale(option)}
              hreflang={option}
              lang={option}
              aria-current={option === locale ? "page" : undefined}
              onClick={(event) => {
                if (option === locale) {
                  event.preventDefault();
                  setOpen(false);
                }
              }}
            >
              {NAME[option]}
              {option === locale ? <CheckIcon size={16} /> : null}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
